import {
  getUserChannel,
  addSocketOpenListener,
  addSocketReplacedListener,
  addUserRejoinListener,
  addSessionEndedListener,
  addPresenceUpdateListener,
  addPresenceSyncListener,
} from './channelService';
import { useChatStore } from '../store/chatStore';
import { useUserPresenceStore } from '../store/userPresenceStore';
import { dmSubjectId } from '../components/chatHelpers';
import { SETTABLE_STATUSES } from '../utils/presenceStatus';

/**
 * The presence protocol, client side. Row 38, Stage 2 (27.08.2026).
 *
 * THIS IS THE ONLY MODULE IN `src/` ALLOWED TO PUSH A `presence:*` EVENT, and
 * the only events it may push are `presence:status` and `presence:watch`.
 * `presence:context` — the per-request broadcasting removed on 03.08.2026 for
 * chattiness — must appear nowhere, ever. `presenceBadgeStaticGuards.test.js`
 * enforces both halves as a source scan over all of `src/`; the guard was
 * NARROWED to this allowlist rather than deleted, because a deleted guard is
 * how the 03.08 regression comes back.
 *
 * Why this is not the 03.08 problem again, structurally rather than hopefully:
 *   - BOUNDED: the watch set is the DM list, capped at 100 ids client-side and
 *     100 server-side. It is not "everyone in the workspace".
 *   - AUTHORISED: the server re-derives entitlement for every id and silently
 *     omits the rest. The client's list is a request, not a grant.
 *   - HUMAN-RATE: a status change is a menu click; a watch resend happens when
 *     the DM list actually changes (debounced) or when the socket re-joins.
 *     The thing that was too chatty fired on every request-tab switch.
 *
 * ── Two pushes ──────────────────────────────────────────────────────────────
 *   presence:status  { status }      -> {ok} | {error, reason}
 *   presence:watch   { user_ids: [] } -> {ok, watching: n} | {error, reason}
 * ── Two subscriptions ───────────────────────────────────────────────────────
 *   presence:update  { user_id, status }
 *   presence:sync    { statuses: { "<id>": status } }
 *
 * `presence:sync` can arrive BEFORE the reply to the `presence:watch` that
 * caused it (Stage 1 contract, addition 3), so nothing here waits for a reply
 * before accepting a snapshot.
 */

/** Server cap (Stage 1). 101 ids is answered `too_many`, so never send 101. */
export const MAX_WATCHED = 100;

/**
 * Coalesce a burst of DM-list changes into one push. `chatStore.channels` is
 * rewritten on every unread recount and every message, so an undebounced
 * resend would push on traffic rather than on membership.
 */
export const WATCH_DEBOUNCE_MS = 400;

let watchTimer = null;
let lastWatchKey = null;
let lastPushedStatus = null;
let socketUnsubs = null;
let chatUnsub = null;

/** The ids we want statuses for: the other party of every 1:1, minus you. */
export function watchIdsFromChatState(state) {
  const selfId = state?.currentUserId;
  const seen = new Set();
  for (const channel of state?.channels || []) {
    if (!channel || channel.kind !== 'dm') continue;
    const id = dmSubjectId(channel, selfId);
    // A note-to-self DM resolves to YOU. Your own badge is served by
    // `userStatusStore` (it must work with no socket at all), so watching
    // yourself would spend a capped slot to learn something you already know.
    if (id == null || (selfId != null && String(id) === String(selfId))) continue;
    seen.add(String(id));
  }
  // Channel order is the store's order (most recently active first), so the
  // truncation drops the conversations furthest from the user's attention.
  return [...seen].slice(0, MAX_WATCHED);
}

function keyOf(ids) {
  return ids.join(',');
}

function sendWatchNow() {
  if (watchTimer) {
    clearTimeout(watchTimer);
    watchTimer = null;
  }
  const channel = getUserChannel();
  const ids = watchIdsFromChatState(useChatStore.getState());
  const key = keyOf(ids);
  if (!channel) {
    // No socket (free plan, logged out, connecting). Forget the key so the
    // same set is genuinely re-sent once a channel exists.
    lastWatchKey = null;
    return;
  }
  if (ids.length === 0) {
    // Nothing to watch. Do not push an empty list on a loop; just remember it.
    lastWatchKey = key;
    return;
  }
  lastWatchKey = key;
  channel
    .push('presence:watch', { user_ids: ids })
    .receive('error', (err) => console.warn('presence:watch refused:', err));
}

function scheduleWatch() {
  const ids = watchIdsFromChatState(useChatStore.getState());
  if (keyOf(ids) === lastWatchKey) return; // the DM list did not actually change
  if (watchTimer) clearTimeout(watchTimer);
  watchTimer = setTimeout(sendWatchNow, WATCH_DEBOUNCE_MS);
}

/**
 * Push this device's status. Called by `userStatusStore.setStatus`.
 *
 * A no-op without a socket, on purpose and permanently: the free plan is
 * local-only and never has one, and the status still has to work there — which
 * is why `userStatusStore` keeps writing localStorage regardless of this call.
 */
export function pushStatus(status) {
  if (!SETTABLE_STATUSES.includes(status)) return;
  lastPushedStatus = status;
  const channel = getUserChannel();
  if (!channel) return;
  channel
    .push('presence:status', { status })
    .receive('error', (err) => console.warn('presence:status refused:', err));
}

/**
 * A socket just became usable (first open, region failover, re-join after a
 * gap). Two things must happen, and the first one is easy to forget:
 *
 * 1. RE-PUSH the status. The server tracks `status: "online"` on every connect,
 *    so a user who set "busy" and then reconnected would silently be advertised
 *    as online for the rest of the session.
 * 2. RE-SEND the watch set. `presence:watch` subscribes a CHANNEL PROCESS; a
 *    reconnect is a new process holding no subscriptions, and nothing is
 *    replayed.
 */
function onSocketReady() {
  if (lastPushedStatus) pushStatus(lastPushedStatus);
  lastWatchKey = null;
  sendWatchNow();
}

function ensureSocketListeners() {
  if (socketUnsubs) return;
  socketUnsubs = [
    addSocketOpenListener(onSocketReady),
    addSocketReplacedListener(onSocketReady),
    addUserRejoinListener(onSocketReady),
  ];
}

// The DM list is the watch set, so its store IS the trigger. Subscribed to the
// whole state (zustand v5 has no selector-subscribe without middleware) and
// filtered by `lastWatchKey` — cheap, and the debounce absorbs the rest.
function ensureChatSubscription() {
  if (chatUnsub) return;
  chatUnsub = useChatStore.subscribe(scheduleWatch);
}

ensureSocketListeners();
ensureChatSubscription();

// `disconnectSocket()` clears socketOpenHandlers/socketReplacedHandlers/
// userRejoinHandlers, and our guard would then keep us from ever re-registering
// — the exact "guard-only was not enough" failure documented on
// `addSessionEndedListener`. So reset the guard AND re-register here, and drop
// every other person's status: it belongs to the session that just ended, and
// after a logout we know nothing about anybody again.
addSessionEndedListener(() => {
  if (watchTimer) clearTimeout(watchTimer);
  watchTimer = null;
  lastWatchKey = null;
  lastPushedStatus = null;
  socketUnsubs = null;
  ensureSocketListeners();
  useUserPresenceStore.getState().clearPresence();
});

addPresenceUpdateListener((payload) => {
  useUserPresenceStore.getState().applyUpdate(payload?.user_id, payload?.status);
});

addPresenceSyncListener((payload) => {
  // ONE `set()` for the whole snapshot — see userPresenceStore's re-render note.
  useUserPresenceStore.getState().applySync(payload?.statuses);
});

/** Test seam: reset module state without a socket teardown. Not used by src/. */
export function __resetUserPresenceForTests() {
  if (watchTimer) clearTimeout(watchTimer);
  watchTimer = null;
  lastWatchKey = null;
  lastPushedStatus = null;
  useUserPresenceStore.getState().clearPresence();
}
